import { ReactNode, useState, useCallback, useMemo, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Components,
  ComputeItemKey,
  ItemContent,
  Virtuoso,
} from 'react-virtuoso';
import { SearchResult, SearchResultRow } from '../../../hooks/useMapSearch';
import { useViewportFillHeight } from './useViewportFillHeight';

type VirtualizedSearchResultsList = {
  results: SearchResult[];
  searchTerm: string;
  onBeforeClick?: () => void;
  header?: ReactNode;
};

type VirtuosoContext = { header?: ReactNode };

// Rough per-row height Virtuoso assumes before rows are measured, so the
// customScrollParent (mobile) path has a scroll range on first render.
const ESTIMATED_ROW_HEIGHT_PX = 120;

// Virtuoso can only render custom content inside its own scroller via a
// component slot (here `Header`). It must be module-level with its data
// passed through Virtuoso's `context`, so the header travels in the `context`
// object and this stable component renders it at the top of the scroll area.
const VirtuosoListHeader: Components<
  SearchResult,
  VirtuosoContext
>['Header'] = ({ context }) => context?.header ?? null;

const VIRTUOSO_LIST_COMPONENTS: Components<SearchResult, VirtuosoContext> = {
  Header: VirtuosoListHeader,
};

// Small breathing room below the list so the last row clears any bottom inset.
const BOTTOM_INSET_PX = 8;

// Rows to keep mounted just outside the viewport. Kept modest on purpose: this
// list exists to bound how many heavy rows are mounted at once (iOS memory), so
// a large overscan would defeat the point.
const VIEWPORT_OVERSCAN_PX = 400;

export const VirtualizedSearchResultsList = (
  props: VirtualizedSearchResultsList,
): JSX.Element => {
  const { results, searchTerm, onBeforeClick, header } = props;

  const theme = useTheme();

  // iPad and up render the persistent right-side Drawer, whose scroll container
  // is a plain overflow:auto Box. Driving Virtuoso off that foreign scroller
  // (customScrollParent) kills iOS momentum scrolling — a flick stops instantly
  // and only advances a row or two — because Virtuoso repositions the scroller's
  // contents mid-flick. On that path we instead give Virtuoso its OWN bounded
  // scroller, which iOS handles smoothly. Phones keep customScrollParent so the
  // bottom sheet's expandOnContentDrag still works.
  const useOwnScroller = useMediaQuery(theme.breakpoints.up('tablet'));

  const desktopWrapperRef = useRef<HTMLDivElement | null>(null);

  // --- customScrollParent mode (mobile bottom sheet) -----------------------
  const [mobileScrollParent, setMobileScrollParent] = useState<HTMLElement>();

  const findScrollParent = useCallback((node: HTMLDivElement | null): void => {
    for (let el = node?.parentElement; el; el = el.parentElement) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        setMobileScrollParent(el);
        return;
      }
    }
  }, []);

  // --- own-scroller mode (desktop / iPad persistent drawer) ----------------
  // Bound Virtuoso's height to the space between the list's top and the bottom
  // of the (full-height) drawer, so Virtuoso becomes the sole scroller without
  // the outer Box (shared with the search bar, tabs, and photo moderation)
  // needing to change. Measured against the viewport bottom, NOT the outer Box:
  // that Box is `overflow:auto; maxHeight:100%`, so it shrinks to content and
  // its bottom would collapse onto the list's top before Virtuoso has height —
  // a deadlock that renders the list empty.
  const desktopOwnScrollerHeight = useViewportFillHeight(desktopWrapperRef, {
    enabled: useOwnScroller,
    bottomInset: BOTTOM_INSET_PX,
  });

  const renderItemContent: ItemContent<SearchResult, VirtuosoContext> =
    useCallback(
      (_, result) => (
        <SearchResultRow
          searchTerm={searchTerm}
          searchResult={result}
          onBeforeClick={onBeforeClick}
        />
      ),
      [searchTerm, onBeforeClick],
    );

  const computeItemKey: ComputeItemKey<SearchResult, VirtuosoContext> =
    useCallback((_, result) => result.id, []);

  const context = useMemo<VirtuosoContext>(() => ({ header }), [header]);

  if (useOwnScroller) {
    return (
      <div ref={desktopWrapperRef}>
        {desktopOwnScrollerHeight !== undefined && (
          <Virtuoso
            data={results}
            style={{ height: desktopOwnScrollerHeight }}
            increaseViewportBy={VIEWPORT_OVERSCAN_PX}
            computeItemKey={computeItemKey}
            itemContent={renderItemContent}
            context={context}
            components={VIRTUOSO_LIST_COMPONENTS}
            skipAnimationFrameInResizeObserver
          />
        )}
      </div>
    );
  }

  return (
    <div ref={findScrollParent}>
      {mobileScrollParent && (
        <Virtuoso
          data={results}
          customScrollParent={mobileScrollParent}
          increaseViewportBy={VIEWPORT_OVERSCAN_PX}
          computeItemKey={computeItemKey}
          itemContent={renderItemContent}
          context={context}
          components={VIRTUOSO_LIST_COMPONENTS}
          skipAnimationFrameInResizeObserver
          defaultItemHeight={ESTIMATED_ROW_HEIGHT_PX}
        />
      )}
    </div>
  );
};
